import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
    FiArrowLeft,
    FiCheck,
    FiClock,
    FiMail,
    FiMessageSquare,
    FiPhone,
    FiSend,
    FiUser
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiUrl } from '../../config/api.config';

const EnquiryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChatDetails();
  }, [id]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    if (!chat?._id) return;

    const pollChat = async () => {
      try {
        const token = localStorage.getItem('agent_token');
        const response = await fetch(getApiUrl(`/chats/${id}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.data?.messages?.length !== chat?.messages?.length) {
            setChat(data.data);
          }
        }
      } catch (error) {
        // silently fail
      }
    };

    const interval = setInterval(pollChat, 5000);
    return () => clearInterval(interval);
  }, [id, chat?._id, chat?.messages?.length]);

  useEffect(() => {
    scrollToBottom();
  }, [chat?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/chats/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setChat(data.data);
      } else {
        console.error('Failed to fetch chat:', response.status);
        toast.error('Failed to load chat details');
      }
    } catch (error) {
      console.error('Error fetching chat details:', error);
      toast.error('Failed to load chat details');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || sending) return;

    try {
      setSending(true);
      const token = localStorage.getItem('agent_token');
      const response = await fetch(getApiUrl(`/chats/${id}/messages`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: replyText.trim() })
      });

      if (response.ok) {
        setReplyText('');
        toast.success('Reply sent!');
        await fetchChatDetails(); // Reload to show new message
      } else {
        toast.error('Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const formatMsgTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) +
           ' · ' +
           date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-gray-600">Loading chat details...</p>
        </div>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Chat not found</p>
        <button
          onClick={() => navigate('/dashboard/enquiries')}
          className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Back to Enquiries
        </button>
      </div>
    );
  }

  const buyer = chat.buyer || {};
  const car = chat.car || {};
  const buyerName = [buyer.firstName, buyer.lastName].filter(Boolean).join(' ') || 'Unknown Buyer';
  const buyerEmail = buyer.email || 'N/A';
  const buyerPhone = buyer.phone || buyer.phoneNumber || 'N/A';
  const carName = car.title || `${car.brand || ''} ${car.model || ''}`.trim() || 'Unknown Car';
  const carImage = car.images?.[0]?.url || car.images?.[0] || null;
  const messages = chat.messages || [];
  const { date: receivedDate, time: receivedTime } = formatDateTime(chat.createdAt);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard/enquiries')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold gradient-text">Chat Details</h1>
            <p className="mt-1 text-gray-600">{buyerName} · {carName}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
          chat.status === 'active' 
            ? 'bg-yellow-100 text-yellow-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {chat.status === 'active' ? <FiClock className="w-4 h-4" /> : <FiCheck className="w-4 h-4" />}
          {chat.status === 'active' ? 'Active' : 'Closed'}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content — Chat Messages */}
        <div className="lg:col-span-2 space-y-6">
          {/* Messages Thread */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden"
          >
            <div className="p-4 bg-gradient-to-r from-orange-500 to-orange-600">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <FiMessageSquare className="w-5 h-5" />
                Messages ({messages.length})
              </h2>
            </div>

            <div className="max-h-[500px] overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <FiMessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No messages yet</p>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isBuyer = msg.senderRole === 'buyer';
                  const isAgentMessage = msg.senderRole === 'agent';
                  const senderLabel = isBuyer ? buyerName : (isAgentMessage ? 'You (Agent)' : 'Seller');
                  return (
                    <div key={msg._id || idx} className={`flex ${isBuyer ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                        isBuyer 
                          ? 'bg-white border border-gray-200 rounded-bl-sm' 
                          : 'bg-orange-500 text-white rounded-br-sm'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${isBuyer ? 'text-orange-600' : 'text-orange-100'}`}>
                            {senderLabel}
                          </span>
                          {isAgentMessage && (
                            <span className={`text-[10px] font-semibold ${isBuyer ? 'text-orange-500' : 'text-orange-100'}`}>
                              (by agent)
                            </span>
                          )}
                        </div>
                        <p className={`text-sm leading-relaxed ${isBuyer ? 'text-gray-800' : 'text-white'}`}>
                          {msg.text}
                        </p>
                        <p className={`text-xs mt-1 text-right ${isBuyer ? 'text-gray-400' : 'text-orange-100'}`}>
                          {formatMsgTime(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                  placeholder="Type a reply..."
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                />
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sending}
                  className={`px-4 py-2.5 rounded-full flex items-center gap-2 font-medium transition-all ${
                    replyText.trim() && !sending
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-md'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <FiSend className="w-4 h-4" />
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Buyer Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl p-6 shadow-soft border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiUser className="w-5 h-5" />
              Buyer Information
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg">
                  <FiUser className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-semibold text-gray-900">{buyerName}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <FiMail className="w-4 h-4 text-gray-600" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{buyerEmail}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <FiPhone className="w-4 h-4 text-gray-600" />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-medium text-gray-900">{buyerPhone}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <a
                  href={`mailto:${buyerEmail}`}
                  className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                >
                  <FiMail className="w-3.5 h-3.5" />
                  Email
                </a>
                <a
                  href={`tel:${buyerPhone}`}
                  className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                >
                  <FiPhone className="w-3.5 h-3.5" />
                  Call
                </a>
              </div>
            </div>
          </motion.div>

          {/* Car Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-soft border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Car Details</h2>
            {carImage && (
              <img
                src={carImage}
                alt={carName}
                className="w-full h-40 object-cover rounded-lg mb-3"
              />
            )}
            <h3 className="text-lg font-bold text-gray-900 mb-1">{carName}</h3>
            {car.price && (
              <p className="text-xl font-bold gradient-text mb-3">
                ₹{(car.price / 100000).toFixed(1)}L
              </p>
            )}
            <button
              onClick={() => navigate(`/dashboard/cars/${car._id}`)}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
            >
              View Car Details
            </button>
          </motion.div>

          {/* Timeline */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-soft border border-gray-100"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="p-2 bg-green-100 rounded-full">
                    <FiMessageSquare className="w-4 h-4 text-green-600" />
                  </div>
                  {messages.length > 1 && <div className="w-px h-full bg-gray-200 mt-2"></div>}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Chat Started</p>
                  <p className="text-xs text-gray-500">{receivedDate}</p>
                  <p className="text-xs text-gray-500">{receivedTime}</p>
                </div>
              </div>
              {messages.length > 1 && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <FiClock className="w-4 h-4 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{messages.length} messages exchanged</p>
                    <p className="text-xs text-gray-500">
                      Last: {formatMsgTime(messages[messages.length - 1]?.createdAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryDetails;
